import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

// hex (#rrggbb) -> "H S% L%" triplet for our CSS variables
function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default function ProvisionTenantDialog({ plans, features, onProvisioned }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", subdomain: "", plan_id: plans[0]?.id || "plan_pro",
    app_title: "", logo_text: "", tagline: "",
    primary_hex: "#0f766e", accent_hex: "#fb7185",
    owner_name: "", owner_email: "", owner_password: "",
  });
  const selectedPlan = plans.find((p) => p.id === form.plan_id);
  const [enabled, setEnabled] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const enabledFeatures = features
        .filter((f) => !f.internal_only && selectedPlan?.feature_keys.includes(f.key) && enabled[f.key])
        .map((f) => f.key);
      const { data } = await api.post("/platform/tenants", {
        name: form.name,
        subdomain: form.subdomain,
        plan_id: form.plan_id,
        owner_name: form.owner_name,
        owner_email: form.owner_email,
        owner_password: form.owner_password,
        enabled_features: enabledFeatures,
        branding: {
          app_title: form.app_title || form.name,
          logo_text: form.logo_text || form.name,
          logo_icon: "Building2",
          tagline: form.tagline,
          primary: hexToHsl(form.primary_hex),
          accent: hexToHsl(form.accent_hex),
          radius_card: "12px",
          radius_button: "8px",
        },
      });
      toast.success(`${data.name} provisioned`);
      setOpen(false);
      onProvisioned?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to provision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-provision-dialog" className="gap-2">
          <Plus className="h-4 w-4" /> Provision operator
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Provision a new operator</DialogTitle>
          <DialogDescription>
            Sales-to-live in minutes: name, plan, branding, owner. No code change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Operator name</Label>
              <Input data-testid="provision-name" value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="Coastal E-Bikes" />
            </div>
            <div className="space-y-1.5">
              <Label>Subdomain</Label>
              <Input data-testid="provision-subdomain" value={form.subdomain}
                onChange={(e) => set("subdomain", e.target.value)} placeholder="coastal" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Plan</Label>
            <div className="flex gap-2">
              {plans.map((p) => (
                <button key={p.id} type="button" data-testid={`provision-plan-${p.id}`}
                  onClick={() => set("plan_id", p.id)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    form.plan_id === p.id ? "border-foreground bg-foreground text-background" : "border-border hover:bg-muted"
                  }`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Primary color</Label>
              <div className="flex items-center gap-2">
                <input data-testid="provision-primary-color" type="color" value={form.primary_hex}
                  onChange={(e) => set("primary_hex", e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent" />
                <span className="text-sm text-muted-foreground">{form.primary_hex}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Accent color</Label>
              <div className="flex items-center gap-2">
                <input data-testid="provision-accent-color" type="color" value={form.accent_hex}
                  onChange={(e) => set("accent_hex", e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent" />
                <span className="text-sm text-muted-foreground">{form.accent_hex}</span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tagline</Label>
            <Input data-testid="provision-tagline" value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)} placeholder="Premium e-bike fleet" />
          </div>

          <div className="rounded-lg border border-border p-3">
            <p className="mb-2 text-sm font-medium">Features (within {selectedPlan?.name} plan)</p>
            <div className="space-y-2">
              {features.filter((f) => !f.internal_only).map((f) => {
                const inPlan = selectedPlan?.feature_keys.includes(f.key);
                return (
                  <div key={f.key} className="flex items-center justify-between">
                    <span className={`text-sm ${inPlan ? "" : "text-muted-foreground"}`}>{f.label}</span>
                    <Switch data-testid={`provision-feature-${f.key}`} disabled={!inPlan}
                      checked={!!enabled[f.key] && inPlan}
                      onCheckedChange={(v) => setEnabled((s) => ({ ...s, [f.key]: v }))} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Owner name</Label>
              <Input data-testid="provision-owner-name" value={form.owner_name}
                onChange={(e) => set("owner_name", e.target.value)} placeholder="Ravi" />
            </div>
            <div className="space-y-1.5">
              <Label>Owner email</Label>
              <Input data-testid="provision-owner-email" type="email" value={form.owner_email}
                onChange={(e) => set("owner_email", e.target.value)} placeholder="owner@coastal.app" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Owner password</Label>
            <Input data-testid="provision-owner-password" type="text" value={form.owner_password}
              onChange={(e) => set("owner_password", e.target.value)} placeholder="min 6 characters" />
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="provision-submit" onClick={submit} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create operator
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
